import { DOCUMENT, EnvironmentProviders, inject, makeEnvironmentProviders, provideAppInitializer } from '@angular/core';
import { focusControl, getActiveElement, getTabStops, indexOfTabStop, isPlainTab } from './focus-utils';

/** Options for {@link provideHxKeyboardNavigation}. */
export interface HxKeyboardNavigationOptions {
  /**
   * Selector describing the containers that define a tab scope. The nearest matching ancestor of the focused element
   * is used to build the tab sequence; when nothing matches, the whole document is used.
   *
   * @default 'form, [hxTabScope], [role="dialog"], [role="tabpanel"]'
   */
  scopeSelector?: string;

  /**
   * Selector describing regions that manage their own Tab handling (grids, dropdown panels, date pickers, …). Key
   * presses originating inside one of these are left untouched.
   *
   * @default '[data-hx-tab-self-managed]'
   */
  selfManagedSelector?: string;
}

const DEFAULT_SCOPE_SELECTOR = 'form, [hxTabScope], [role="dialog"], [role="tabpanel"]';
const DEFAULT_SELF_MANAGED_SELECTOR = '[data-hx-tab-self-managed]';

/**
 * Installs application-wide logical `Tab` navigation.
 *
 * A single capture-phase `keydown` listener makes `Tab`/`Shift+Tab` move focus to the next applicable editable control
 * in the visual and functional order of the page, skipping disabled, hidden and read-only controls. Components that
 * implement their own keyboard model (grids, dropdown panels, …) are left alone, and the browser's native behaviour
 * still applies at the boundaries of each scope.
 *
 * Register it once per application:
 *
 * @example
 *   ```typescript
 *   bootstrapApplication(AppComponent, {
 *     providers: [provideHxKeyboardNavigation()],
 *   });
 *   ```;
 */
export function provideHxKeyboardNavigation(options: HxKeyboardNavigationOptions = {}): EnvironmentProviders {
  const scopeSelector = options.scopeSelector ?? DEFAULT_SCOPE_SELECTOR;
  const selfManagedSelector = options.selfManagedSelector ?? DEFAULT_SELF_MANAGED_SELECTOR;

  return makeEnvironmentProviders([
    provideAppInitializer(() => {
      const document = inject(DOCUMENT);

      const onKeydown = (event: KeyboardEvent): void => {
        if (!isPlainTab(event) || event.defaultPrevented) {
          return;
        }

        const active = getActiveElement(document);
        if (!active) {
          return;
        }

        // Composite controls own their internal Tab handling.
        if (active.closest(selfManagedSelector)) {
          return;
        }

        const scope = (active.closest(scopeSelector) as HTMLElement | null) ?? document.body;
        const controls = getTabStops(scope);
        const currentIndex = indexOfTabStop(scope, controls, active);
        if (currentIndex === -1) {
          return;
        }

        const nextIndex = currentIndex + (event.shiftKey ? -1 : 1);
        if (nextIndex < 0 || nextIndex >= controls.length) {
          // Boundary of the scope: preserve the browser's native behaviour.
          return;
        }

        const next = controls[nextIndex];
        if (!next) {
          return;
        }

        event.preventDefault();
        focusControl(next);
      };

      // Bubble phase so component-level handlers (grids, dropdowns) get the first chance to handle the key.
      document.addEventListener('keydown', onKeydown);
    }),
  ]);
}
